import { useCallback, useMemo, useRef, useState } from "react";
import { ReportType } from "~/features/cart/cart/components/Buttons/DownloadReportButton/api/downloadCartReport";
import { DownloadClickLink } from "~/common/components/Link";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import Spinner from "~/common/components/Spinner/Spinner";
import { toMapFn } from "~/common/utils/reducers";
import { QuoteListTable } from "~/features/orders/quoteList/components/QuoteListTable/QuoteListTable";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import {
  downloadQuoteHistoryReport,
  getReportLabels,
} from "~/features/orders/quoteList/api/downloadQuoteHistoryReport";
import { type Subscription } from "rxjs";
import { getIsAllOpen, rowHasSurcharge } from "~/features/orders/orderList/OrderListPage";
import {
  OrderListSearchBox,
  type OrderListSearchBoxData,
} from "~/features/orders/orderList/components/OrderListSearchBox/OrderListSearchBox";
import classes from "~/features/orders/orderList/OrderListPage.module.less";
import {
  filterProductsMO,
  type GetOrderListSearchResults,
  getQuoteListSearchResults,
  transformFormData,
} from "~/features/orders/common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { SearchBoxVariant } from "~/features/orders/orderList/components/OrderListSearchBox/SearchBoxVariant";
import { fetchQuoteDetails } from "~/features/orders/quoteList/api/fetchOrderDetails/fetchQuoteDetails";
import { useQuoteIsBundleInfoModal } from "~/features/orders/quoteList/components/QuoteIsBundleInfoModal";
import { useTranslation } from "react-i18next";
import { transformQuoteDetails } from "~/features/orders/quoteList/api/fetchOrderDetails/v2/transformQuoteDetails";
import { MultiAccountSearchDataProvider } from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";
import {
  canStartLoadingDetails,
  quoteListPageActions,
  type QuoteListRow,
  useQuoteListState,
} from "~/features/orders/quoteList/useQuoteListState";
import { Button } from "~/common/mui";
import { useQuoteListSelectionState } from "~/features/orders/common/components/v2/QuoteTable/useQuoteListSelectionState";
import { canQuoteListRowBeSelected, makeMapQuoteLineToATCAPIItem } from "~/features/orders/quoteList/utils";
import type { AddToCartApiItem } from "~/features/cart/cart/api/AddToCart";
import { sendDataMultipleQuotes } from "~/features/cart/cart/api/MultipleQuoteToCart";
import { addMessage } from "~/features/toasts/redux/actions";
import { useDispatch } from "react-redux";
import type { QuoteDetailsSelectionState } from "~/features/orders/common/components/v2/QuoteTable/useQuoteDetailsSelectionState";
import { openModal } from "~/common/utils/ModalService";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { isErrorEntry } from "~/features/orders/common/utils/util";
import { Alert } from "~/common/mui/Alert/Alert";
import { ATCBar } from "~/features/orders/common/components/ATCBar";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import type { QuoteLineMO } from "~/features/orders/common/components/v2/models";

interface selectedATCItemsProps {
  selectedLineItems: number;
  selectedQuantity: number;
  totalPrice: PriceData;
}

const selectedATCItemsDefaultValue = {
  selectedLineItems: 0,
  selectedQuantity: 0,
  totalPrice: { value: 0, currencyIso: "" },
};

export const isFromDifferentAccOrSalesOrg = (row: QuoteListRow) => {
  return (
    (row.data.account?.code && row.data.account?.code !== window.SCHA.user.selectedAccount.uid) ||
    (row.data.account?.salesOrg && row.data.account?.salesOrg !== window.SCHA.user.selectedAccount.salesOrg)
  );
};

function hasRowAnythingSelected(row: QuoteListRow, selectionContext: Record<string, QuoteDetailsSelectionState>) {
  const documentState = selectionContext[row.data.soNumber];
  return documentState?.isAnythingSelected ?? false;
}

export const QuoteListPage = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useQuoteListState();
  const reduxDispatch = useDispatch();
  const selectionContext = useQuoteListSelectionState();
  const { loading, error, results: rows } = state;
  const subscriptionRef = useRef<Subscription | null>(null);
  const submittedFormData = useRef<GetOrderListSearchResults | null>(null);

  const isAllOpen = getIsAllOpen(state.results);
  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const [isATCInProgress, setIsATCInProgress] = useState(false);

  const selectedATCItems = useMemo(
    () => getSelectedATCItems(rows, selectionContext.selectionPerDocument),
    [rows, selectionContext],
  );

  const { requestConfirmation } = useQuoteIsBundleInfoModal();

  const onSubmitHandler = useCallback((formData: OrderListSearchBoxData) => {
    subscriptionRef.current?.unsubscribe();
    const transformedData = transformFormData(formData);
    submittedFormData.current = transformedData;
    dispatch(quoteListPageActions.beginLoading());
    subscriptionRef.current = getQuoteListSearchResults(transformedData).subscribe({
      error: () => dispatch(quoteListPageActions.errorLoading()),
      next: (data) => dispatch(quoteListPageActions.addResultPage(data)),
      complete: () => dispatch(quoteListPageActions.finishLoading()),
    });
  }, []);

  const loadSingleDetail = useCallback(async (row: QuoteListRow, preselect: boolean) => {
    const soNumber = row.data.soNumber;
    let accountNumber, salesOrg;
    if (isFromDifferentAccOrSalesOrg(row)) {
      accountNumber = row.data.account?.code;
      salesOrg = row.data.account?.salesOrg;
    }
    try {
      const dto = await fetchQuoteDetails(soNumber, accountNumber, salesOrg);
      const data = transformQuoteDetails(dto);
      reduxDispatch(addBaseProductDataAction(data.quoteLines.map((line) => line.product)));
      data.quoteLines = filterProductsMO(data.quoteLines, submittedFormData.current);
      selectionContext.init(data, preselect);
      dispatch(quoteListPageActions.finishLoadingDetails(data));
    } catch {
      dispatch(quoteListPageActions.errorLoadingDetails(soNumber));
    }
  }, []);

  const toggleOpenCb = useCallback(
    (row: QuoteListRow) => {
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row, false);
      }
      dispatch(quoteListPageActions.toggleOpen(row.data.soNumber));
    },
    [loadSingleDetail],
  );

  const toggleOpenAllCb = useCallback(() => {
    rows.forEach((row) => {
      const newState = !isAllOpen;
      if (newState && canStartLoadingDetails(row) && !isErrorEntry(row)) {
        // TODO - fetch service?
        loadSingleDetail(row, false);
      }
    });
    dispatch(quoteListPageActions.toggleAllOpen());
  }, [isAllOpen, loadSingleDetail, rows]);

  const toggleSelectAllQuotes = useCallback(() => {
    rows.forEach((row) => {
      if (!canQuoteListRowBeSelected(row)) {
        return;
      }
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row, true);
        if (!row.open) {
          dispatch(quoteListPageActions.toggleOpen(row.data.soNumber));
        }
      } else {
        selectionContext.toggleAllLines(row.data.soNumber);
      }
    });
  }, [rows, loadSingleDetail, selectionContext.toggleAllLines]);

  const toggleSelectLineItem = useCallback(
    async (soNumber: string, quoteLineNo: string) => {
      const quote = rows.find((item) => item.data.soNumber === soNumber)!;
      if (quote.data.isBundled) {
        const confirmation = await requestConfirmation();
        if (confirmation) {
          selectionContext.toggleAllLines(soNumber);
        }
      } else {
        selectionContext.toggleLine(soNumber, quoteLineNo);
      }
    },
    [rows, selectionContext.toggleAllLines, selectionContext.toggleLine],
  );

  const toggleSelectQuote = useCallback(
    (row: QuoteListRow) => {
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row, true);
        if (!row.open) {
          dispatch(quoteListPageActions.toggleOpen(row.data.soNumber));
        }
        return;
      } else {
        selectionContext.toggleAllLines(row.data.soNumber);
      }
    },
    [loadSingleDetail, selectionContext.toggleAllLines],
  );

  const downloadQuoteList = (fileType: ReportType) => {
    const reportLabels = toMapFn(
      getReportLabels(),
      (key) => key,
      (key) => t(key),
    );
    downloadQuoteHistoryReport(rows, fileType, reportLabels);
  };

  const disableCheckoutBtn = !rows.some((row) => hasRowAnythingSelected(row, selectionContext.selectionPerDocument));

  const containsSurcharge = rows.some(rowHasSurcharge);

  const disableInteractions = loading || error || webservicesDisabled;
  const showRows = !error && rows.length > 0;

  const renderReportButton = (type: ReportType, dataTestIdStr: string, typeStr: string) => (
    <DownloadClickLink
      onClick={() => downloadQuoteList(type)}
      data-test-id={`orderList-seatchBox-btn-${dataTestIdStr}`}
      disabled={disableInteractions}
    >
      {t(`downloadFile.type.${typeStr}`)}
    </DownloadClickLink>
  );

  const addQuotesToCart = async () => {
    const mapLineItem = makeMapQuoteLineToATCAPIItem();
    const quotes: { quoteNumber: string; items: AddToCartApiItem[] }[] = [];
    rows
      .filter((row) => {
        const documentSelectionState = selectionContext.selectionPerDocument[row.data.soNumber];
        return documentSelectionState?.isAnythingSelected ?? false;
      })
      .forEach((row) => {
        const documentSelectionState = selectionContext.selectionPerDocument[row.data.soNumber];
        const selectedLineItemMap = documentSelectionState?.selectedLineNos ?? {};
        const items = row.details!.quoteLines.filter((line) => selectedLineItemMap[line.lineNo]).flatMap(mapLineItem);
        quotes.push({ quoteNumber: row.data.soNumber, items });
      });

    const getAllItems = (
      quotes: {
        quoteNumber: string;
        items: AddToCartApiItem[];
      }[],
    ) => {
      const allItems = quotes.reduce((accumulator, quote) => accumulator.concat(quote.items), [] as AddToCartApiItem[]);
      return allItems;
    };

    setIsATCInProgress(true);

    if (quotes.length > 0) {
      try {
        const response = await sendDataMultipleQuotes(quotes);
        if (response.ok) {
          openModal("add-to-cart-dialog", (close) => (
            <CartSuccessModal formData={getAllItems(quotes)} onClose={close} page="QUOTE_LIST" />
          ));
          //reduxDispatch(addMessage("info", t("page.quoteDetails.addToCart.success")));
        } else {
          reduxDispatch(addMessage("error", t("page.quoteDetails.errorSending")));
        }
      } catch {
        reduxDispatch(addMessage("error", t("page.quoteDetails.errorSending")));
      } finally {
        setIsATCInProgress(false);
      }
    }
  };

  return (
    <div className="scha-order-history-page">
      <PageTitle>{t("quoteListPage.title")}</PageTitle>

      {loading && <Spinner className="mb-3" />}

      {showRows && (
        <h2 className={classes.total} data-test-id={"quotesAmount"}>
          {t("quoteListPage.quotesAmount", { count: rows?.length ?? 0 })}
        </h2>
      )}

      <OrderListSearchBox onSubmit={onSubmitHandler} variant={SearchBoxVariant.QUOTE} />

      <div className="d-flex flex-justify-end mb-30">
        <Button
          className={"ml-4"}
          data-test-id={"add-all-to-cart"}
          disabled={disableCheckoutBtn || disableInteractions}
          onClick={addQuotesToCart}
        >
          {t("quoteDetailsPage.checkout")}
        </Button>
      </div>

      {!loading && rows.length > 0 && (
        <div className={classes.reportButtonContainer}>
          <span className="scha__order-list__download-title">{t("quoteListPage.downloads.title")}</span>
          {renderReportButton(ReportType.CSV, "downloadCSV", "csv")}
          {renderReportButton(ReportType.XLSX, "downloadExcel", "excel")}
        </div>
      )}

      {showRows && (
        <QuoteListTable
          rows={rows}
          toggleOpen={toggleOpenCb}
          toggleOpenAll={toggleOpenAllCb}
          toggleSelectLineItem={toggleSelectLineItem}
          toggleSelectQuote={toggleSelectQuote}
          toggleSelectAllQuotes={toggleSelectAllQuotes}
          selectionPerDocument={selectionContext.selectionPerDocument}

          // toggleSelect={toggleSelect}
          // toggleSelectLineItem={toggleSelectLineItem}
          // toggleSelectAll={toggleSelectAll}
        />
      )}

      {<IncludesSurchargeDisclaimer hasSurcharge={containsSurcharge} className={"mt-30"} />}

      {!webservicesDisabled && !loading && error && <Alert severity="error">{t("quoteListPage.error")}</Alert>}

      {!loading && !error && !rows.length && (
        <h2 className="scha__order-history__total my-0">{t("quoteListPage.noResults")}</h2>
      )}

      {selectedATCItems.selectedLineItems > 0 && (
        <ATCBar
          selectedLineItems={selectedATCItems.selectedLineItems}
          selectedQuantity={selectedATCItems.selectedQuantity}
          totalPrice={selectedATCItems.totalPrice}
          onAddToCart={addQuotesToCart}
          loading={isATCInProgress}
        />
      )}
    </div>
  );
};

export function createQuoteListPage(data: { accounts: string; filters: string }) {
  return (
    <MultiAccountSearchDataProvider accountsJson={data.accounts}>
      <QuoteListPage />
    </MultiAccountSearchDataProvider>
  );
}

const getSelectedATCItems = (
  rows: QuoteListRow[],
  selectionPerDocument: Record<string, QuoteDetailsSelectionState>,
): selectedATCItemsProps => {
  const selectedQuoteLineItems: QuoteLineMO[] = rows.flatMap((row) => {
    const documentSelectionState = selectionPerDocument[row.data.soNumber];
    if (!documentSelectionState?.isAnythingSelected) {
      return [];
    }

    const selectedLineItemMap = documentSelectionState.selectedLineNos ?? {};
    return row.details?.quoteLines.filter((line) => selectedLineItemMap[line.lineNo]) ?? [];
  });

  if (selectedQuoteLineItems.length === 0) {
    return selectedATCItemsDefaultValue;
  }

  const totalPieces = selectedQuoteLineItems.reduce(
    (total, item) => total + item.parts.reduce((sum, part) => sum + part.confirmedQuantity, 0),
    0,
  );

  const currencyIso = selectedQuoteLineItems[0]?.parts[0]?.price?.currencyIso;

  const totalPrice = selectedQuoteLineItems.reduce(
    (total, item) =>
      total +
      item.parts.reduce((sum, part) => {
        const priceData = getTotalPrice(part.price, part.confirmedQuantity);
        if (priceData.currencyIso !== currencyIso) {
          throw new Error("Mismatched currencies in selected items");
        }
        return sum + priceData.value;
      }, 0),
    0,
  );

  return {
    selectedLineItems: selectedQuoteLineItems.length,
    selectedQuantity: totalPieces,
    totalPrice: {
      currencyIso: currencyIso ?? "",
      value: totalPrice,
    },
  };
};
